/**
 * FAQPage structured data (JSON-LD) for SEO/AEO optimization
 * Helps answer engines (Perplexity, ChatGPT) extract Q&A directly
 * Follows Schema.org FAQPage specification
 */

/**
 * FAQ item for FAQSection component and structured data
 *
 * Used to display FAQ questions and answers in an accordion format,
 * and to generate Schema.org FAQPage structured data for SEO.
 *
 * @example
 * ```typescript
 * const faq: FAQItem = {
 *   question: 'What is Email Ferret?',
 *   answer: 'Email Ferret is an AI email assistant that works 24/7 to detect spam and AI-generated cold outreach in your Gmail inbox.'
 * };
 * ```
 */
import React from 'react';
import { JSONLDScript } from './jsonld-script';

export interface FAQItem {
  /** The FAQ question text */
  question: string;
  /** The FAQ answer text */
  answer: string;
}

interface FAQStructuredDataProps {
  faqs: FAQItem[];
  /**
   * Absolute URL of the page the FAQs appear on. Required so each FAQPage
   * node identifies its own page — a shared fallback would make every page's
   * schema point at the same URL.
   */
  url: string;
}

export function FAQStructuredData({ faqs, url }: FAQStructuredDataProps) {
  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
    url,
  };

  return <JSONLDScript data={faqPageSchema} id="faq-structured-data" />;
}
